import localeRegistryData from "./shared/localeRegistry.json";

const defaultLocaleConfig = localeRegistryData.supportedLocales.find(
  (localeConfig) => localeConfig.locale === localeRegistryData.defaultLocale,
);

export const fallbackHomepageData = {
  allRoutesUrl: "https://www.directferries.co.uk/?dfpid=6851&affid=1",
  availableCountries: localeRegistryData.countries.map((country) => ({
    code: country.code,
    name:
      defaultLocaleConfig.countryCode === country.code
        ? defaultLocaleConfig.countryName
        : {
            BE: "Belgium",
            CH: "Switzerland",
            DE: "Germany",
            ES: "Spain",
            FR: "France",
            GB: "United Kingdom",
            GR: "Greece",
            IE: "Ireland",
            IT: "Italy",
            NL: "Netherlands",
          }[country.code] || country.code,
  })),
  availableLanguages: localeRegistryData.languages.map((language) => ({
    code: language.code,
    name: language.nativeName,
  })),
  availableLocales: localeRegistryData.supportedLocales,
  copy: {
    footer: {
      blurb:
        "FerrySearch brings together trusted ferry routes with a simpler, more intuitive way to explore them before you book.",
      liveNote: "Live availability and pricing are provided by Direct Ferries.",
      loadingText: "Refreshing local ferry routes...",
    },
    hero: {
      description:
        "Plan crossings from Britain into France, the Netherlands, Spain, and Ireland with a cleaner search experience wrapped around the live booking engine.",
      eyebrow: "Premium ferry comparison for the UK",
      pills: ["UK departures", "Europe and Ireland", "Personalised routes"],
      primaryCta: "Start searching",
      secondaryCta: "Routes",
      title: "Compare ferry crossings from the UK to Europe and Ireland",
    },
    nav: {
      applyLabel: "Apply",
      brandTagline: "Premium ferry comparison",
      countryLabel: "Country",
      languageLabel: "Language",
      navRoutes: "Routes",
      navSearch: "Search",
      navWhy: "Why FerrySearch",
    },
    routes: {
      description:
        "A curated mix of Britain-to-Europe and Britain-to-Ireland crossings that travellers actually book.",
      heading: "Popular ferry routes from the UK",
      kicker: "Popular ferry routes from the UK",
      routeCta: "Search this route",
      showFewerLabel: "Show fewer routes",
      showMoreLabel: "Show more routes",
      viewAllLabel: "View all routes",
    },
    search: {
      description:
        "Use the live Direct Ferries search inside a cleaner FerrySearch experience built around British departures.",
      kicker: "Search UK departures",
      loadingText: "Loading live ferry search...",
      note: "Live availability inside a calmer, more useful way to compare routes.",
      statusDesktop: "Live availability",
      statusMobile: "Mobile-friendly search",
      title: "Search and book ferries from the UK",
    },
    ui: {
      mobileButton: "Open search",
      mobilePrompt: "Ready to compare routes?",
      routeCreditFallback: "Curated coastal artwork",
    },
    why: {
      countryCode: "GB",
      footer:
        "FerrySearch brings together trusted ferry routes with a simpler, more intuitive way to explore them.",
      heading: "Built for UK road trips and European crossings",
      intro:
        "A simpler, more reliable way to compare ferry routes from Britain to Europe and Ireland without the clutter and confusion of traditional booking sites.",
      points: [
        {
          description:
            "Quick access to the crossings UK travellers use most, from Dover to Calais to overnight sailings into Spain and the Netherlands.",
          title: "See the routes that actually matter",
        },
        {
          description:
            "Find the right route faster with clear options, simple choices, and no unnecessary steps getting in the way.",
          title: "Less friction, more clarity",
        },
        {
          description:
            "Whether it is a weekend in France or a long road trip through Europe, the experience reflects how people actually travel.",
          title: "Designed around real journeys",
        },
        {
          description:
            "Search and compare routes in a cleaner environment before heading into the booking engine with confidence.",
          title: "A better way into live booking",
        },
      ],
    },
  },
  iframe: {
    culture: defaultLocaleConfig.directFerriesCulture,
    defaultRouteSlug: defaultLocaleConfig.defaultRouteSlug,
    destinationPort: defaultLocaleConfig.defaultDestinationPort,
    originPort: defaultLocaleConfig.defaultOriginPort,
  },
  resolvedLocale: {
    ...defaultLocaleConfig,
    browserLanguage: "",
    detectedCountry: "GB",
    detectedLanguage: "en",
    source: "default",
  },
  seo: {
    alternates: {
      "de-CH": "https://www.ferrysearch.co.uk/de-ch",
      "de-DE": "https://www.ferrysearch.co.uk/de-de",
      "el-GR": "https://www.ferrysearch.co.uk/el-gr",
      "en-CH": "https://www.ferrysearch.co.uk/en-ch",
      "en-GB": "https://www.ferrysearch.co.uk/en-gb",
      "en-IE": "https://www.ferrysearch.co.uk/en-ie",
      "es-ES": "https://www.ferrysearch.co.uk/es-es",
      "fr-BE": "https://www.ferrysearch.co.uk/fr-be",
      "fr-CH": "https://www.ferrysearch.co.uk/fr-ch",
      "fr-FR": "https://www.ferrysearch.co.uk/fr-fr",
      "it-IT": "https://www.ferrysearch.co.uk/it-it",
      "nl-BE": "https://www.ferrysearch.co.uk/nl-be",
      "nl-NL": "https://www.ferrysearch.co.uk/nl-nl",
    },
    canonicalUrl: "https://www.ferrysearch.co.uk/en-gb",
    description:
      "Compare ferry crossings from the UK to France, Ireland, Spain, Holland and more with a cleaner, faster ferry search experience.",
    htmlLang: "en-GB",
    og: {
      description:
        "Compare ferry crossings from the UK to France, Ireland, Spain, Holland and more with a cleaner, faster ferry search experience.",
      imageUrl: "https://www.ferrysearch.co.uk/android-chrome-512x512.png",
      locale: "en_GB",
      siteName: "FerrySearch",
      title: "FerrySearch UK | Compare Ferry Routes to Europe and Ireland",
      type: "website",
      url: "https://www.ferrysearch.co.uk/en-gb",
    },
    robots: "noindex, nofollow",
    siteName: "FerrySearch",
    title: "FerrySearch UK | Compare Ferry Routes to Europe and Ireland",
    twitter: {
      card: "summary_large_image",
      description:
        "Compare ferry crossings from the UK to France, Ireland, Spain, Holland and more with a cleaner, faster ferry search experience.",
      imageUrl: "https://www.ferrysearch.co.uk/android-chrome-512x512.png",
      title: "FerrySearch UK | Compare Ferry Routes to Europe and Ireland",
    },
    xDefaultUrl: "https://www.ferrysearch.co.uk/en-gb",
  },
  routes: [
    {
      title: "Dover to Calais",
      slug: "dover_calais",
      href: "https://www.directferries.co.uk/?dfpid=6851&affid=1&rurl=dover_calais_ferry.htm",
      from: "Dover",
      to: "Calais",
      duration: "About 90 minutes",
      tagline: "Fastest classic crossing",
      description:
        "The quickest and most popular way into northern France, with frequent sailings all day.",
      cta: "Search this route",
      tag: "Best seller",
      image: null,
      isValid: true,
    },
    {
      title: "Portsmouth to Caen",
      slug: "portsmouth_caen",
      href: "https://www.directferries.co.uk/?dfpid=6851&affid=1&rurl=portsmouth_caen_ferry.htm",
      from: "Portsmouth",
      to: "Caen",
      duration: "Day and overnight sailings",
      tagline: "Cabin-friendly western France route",
      description:
        "A relaxed sailing into Normandy with excellent onboard facilities and overnight cabin options.",
      cta: "Search this route",
      tag: "Normandy favourite",
      image: null,
      isValid: true,
    },
    {
      title: "Hull to Rotterdam",
      slug: "hull_rotterdam",
      href: "https://www.directferries.co.uk/?dfpid=6851&affid=1&rurl=hull_rotterdam_ferry.htm",
      from: "Hull",
      to: "Rotterdam",
      duration: "Relaxed overnight crossing",
      tagline: "Drive on, wake up abroad",
      description:
        "An easy overnight journey into the Netherlands, ideal for road trips into Europe.",
      cta: "Search this route",
      tag: "Overnight favourite",
      image: null,
      isValid: true,
    },
    {
      title: "Holyhead to Dublin",
      slug: "holyhead_dublin",
      href: "https://www.directferries.co.uk/?dfpid=6851&affid=1&rurl=holyhead_dublin_ferry.htm",
      from: "Holyhead",
      to: "Dublin",
      duration: "Popular all year",
      tagline: "Ireland's most-used crossing",
      description:
        "A reliable and flexible route for family trips, business travel, and Irish road adventures.",
      cta: "Search this route",
      tag: "Ireland essential",
      image: null,
      isValid: true,
    },
  ],
};
